import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Alert, Link, Typography } from "@mui/material";
import { type IHazardClass, useHazardClasses } from "hooks/hazard-class";
import { t } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  sapNumber: string | undefined;
}

enum MessageType {
  NO_CLASSIFICATION = "NO_CLASSIFICATION",
  NOT_FOUND = "NOT_FOUND",
  OTHER = "OTHER",
}

const HazardClass = ({ sapNumber }: Props) => {
  const [results, error] = useHazardClasses(sapNumber);

  const hazardClasses = useMemo(() => {
    if (results?.results) {
      return results.results;
    }

    return [];
  }, [results]);

  const migratedHazardData: IHazardClass[] = useMemo(() => {
    if (hazardClasses?.length) {
      return hazardClasses.map((i) => {
        let color = "#000";

        if (i.messageType.toUpperCase() === MessageType.NO_CLASSIFICATION) {
          color = "#03BB7E";
        }

        if (i.messageType.toUpperCase() === MessageType.NOT_FOUND) {
          color = "#FF7F41";
        }

        if (i.messageType.toUpperCase() === MessageType.OTHER) {
          color = "#E50101";
        }

        return {
          messageType: i.messageType,
          message: i.message,
          profile: i.profile,
          color,
        };
      });
    }

    return [];
  }, [hazardClasses]);

  if (error) {
    return <HazardClassFailed />;
  }

  if (!sapNumber) {
    return <HazardClassWithoutSapNumber />;
  }

  return (
    <HazardClassContent
      migratedHazardData={migratedHazardData}
      sapNumber={sapNumber}
    />
  );
};

export default HazardClass;

interface HazardClassContentProps {
  color?: string;
  migratedHazardData: IHazardClass[];
  sapNumber: string | undefined;
}

function HazardClassContent({
  // biome-ignore lint/correctness/noUnusedFunctionParameters: TobeEvaluated with the team
  color,
  migratedHazardData,
  sapNumber,
}: HazardClassContentProps) {
  const { t } = useTranslation();
  return (
    <>
      <Typography variant="label">{t("regulation_no_1272_2008")}</Typography>
      {migratedHazardData.length ? (
        <ul>
          {migratedHazardData.map((i) => (
            <li style={{ color: i.color }}>{i.message}</li>
          ))}
        </ul>
      ) : (
        ""
      )}

      <Link
        href={`https://solutions.covestro.com/en/productsafetyfirst/sds?SearchDigit=${sapNumber}&SearchName=&SelectedLanguage=&SelectedCountry=`}
        target="_blank"
        underline="none"
        style={{ display: "flex", alignItems: "center" }}
      >
        {t("view_safer_data_sheet")}{" "}
        <OpenInNewIcon sx={{ width: 15, marginLeft: 0.5 }} />
      </Link>
    </>
  );
}

function HazardClassFailed() {
  const { t } = useTranslation();
  return (
    <>
      <Typography variant="label">{t("regulation_no_1272_2008")}</Typography>
      <Alert severity="error" style={{ marginTop: 5 }}>
        {t("failed_message")}
      </Alert>
    </>
  );
}

function HazardClassWithoutSapNumber() {
  return (
    <>
      <Typography variant="label">{t("regulation_no_1272_2008")}</Typography>
      <ul>
        <li style={{ color: "#FF7F41" }}>
          {t("regulation_no_1272_2008_not_evaluated")}
        </li>
      </ul>
    </>
  );
}
